<template>
    <div class="playbook-generation-container">
        <AonSpinner v-if="loading" class="m-auto" :scale="0.5" />
        <div
            v-else
            class="parent-holder display-flex"
            :class="{ 'fit-to-viewport': checkIfFitToViewport }"
        >
            <PlaybookStepTracker
                overwrite-class="no-substeps"
                allow-step-navigation
                heading="Create a new Playbook"
                :hide-on-step="stepToHideSideNav"
                :current-step="currentPlaybookStep"
            />
            <KeepAlive>
                <div
                    v-if="
                        playbookGenerationStore.trackStepsFinal.length === 0 ||
                        playbookGenerationStore.trackStepsFinal[0].isCurrentStep
                    "
                    class="w-100 p-xl"
                >
                    <h5>Select Playbook Type</h5>
                    <AonCard class="mt-xl">
                        <div class="content-holder">
                            <PlaybookTypeSelection />
                        </div>
                    </AonCard>
                </div>
            </KeepAlive>

            <div v-if="playbookComponent" class="p-sm flex-grow-1" style="width: 0">
                <component :is="playbookComponent" />
            </div>
        </div>
        <PlaybookNav />

        <!-- Product alignment loading -->
        <AonCoverLoading
            :loading="playbookGenerationStore.productAlignmentLoading"
            :title="`Please hang tight!`"
            :message="loadingMessage"
        >
            <template #footer>
                <progress
                    v-if="playbookGenerationStore.percentComplete !== 0"
                    :value="playbookGenerationStore.percentComplete"
                    max="100"
                    class="mt-md progress-bar"
                />
            </template>
        </AonCoverLoading>
        <!-- Contender List loading -->
        <AonCoverLoading
            :loading="playbookGenerationStore.contenderListLoading"
            :title="`Loading contender list for ${playbookGenerationStore.targetCompany}`"
            :message="contenderLoadingMessage"
        >
        </AonCoverLoading>
        <!-- Error for product alignment -->
        <AonCoverLoading
            class="error-cover"
            :loading="typeof playbookGenerationStore.error === 'string' ? true : false"
            :show-spinner="false"
            :title="`No Products Found for ${playbookGenerationStore.targetCompany}`"
            :message="playbookGenerationStore.errorMsg"
        >
            <template #header>
                <font-awesome-icon
                    :icon="`fas fa-triangle-exclamation`"
                    style="border-radius: 100%"
                    class="text-warning bg-warning p-md"
                    size="2xl"
                />
            </template>
            <template #footer>
                <ul class="my-md" style="list-style: disc">
                    <li class="my-sm">
                        Select another company in the same hierarchy, such as a parent or subsidiary
                        that may be better known.
                    </li>
                    <li class="my-sm">Choose a similar company, such as a competitor.</li>
                    <li class="my-sm">Build an arena using relevant technology areas.</li>
                </ul>
                <p>
                    If you need further assistance, please contact
                    <a href="mailto:support@moatmetrics.com">support@moatmetrics.com</a>
                </p>
                <div class="button-holder mt-xl w-100 display-flex justify-center">
                    <AonButton
                        class="mr-xl"
                        label="Select a New Target Company"
                        @clicked="resetTargetCompanyStep()"
                    />
                </div>
            </template>
        </AonCoverLoading>
    </div>
</template>

<script setup>
import { computed, ref, onBeforeMount, onMounted } from 'vue'
import { usePlaybookGenerationStore } from '@/stores'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import PlaybookStepTracker from '@/components/playbooks/shared/PlaybookStepTracker.vue'
import PlaybookNav from '@/components/playbooks/shared/PlaybookNav.vue'
import PlaybookTypeSelection from '@/components/playbooks/shared/PlaybookTypeSelection.vue'

import MatchUpPlaybook from '@/components/playbooks/playbookTypes/matchUp/MatchUpPlaybook.vue'
import CounterAssertionPlaybook from '@/components/playbooks/playbookTypes/counterAssertion/CounterAssertionPlaybook.vue'
import LicensingOpportunitiesPlaybook from '@/components/playbooks/playbookTypes/licensingOpportunities/LicensingOpportunitiesPlaybook.vue'
import ProspectivePatentAnalysisPlaybook from '@/components/playbooks/playbookTypes/prospectivePatentAnalysis/ProspectivePatentAnalysisPlaybook.vue'

const playbookGenerationStore = usePlaybookGenerationStore()
const router = useRouter()
const { t } = useI18n()

const loading = ref(true)

onBeforeMount(async () => {
    loading.value = false
})

onMounted(() => {})

const loadingMessage = computed(() => {
    if (playbookGenerationStore.needsNewAlignment) {
        return `We are aligning patents to the product and technology areas for ${playbookGenerationStore.targetCompany}. This process will take 5-10 minutes if not cached in our system. Please feel free to check out the other tabs and this process will continue in the background.`
    } else {
        return `Loading Moat2Product for ${playbookGenerationStore.targetCompany}`
    }
})

const contenderLoadingMessage = computed(() => {
    if (playbookGenerationStore.playbookTypeSelected === 'matchUp') {
        return `We'll apply the top 5 contenders for ${playbookGenerationStore.targetCompany}`
    } else {
        return 'Populating the list of competitors'
    }
})

const playbookComponent = computed(() => {
    if (
        playbookGenerationStore.trackStepsFinal.length === 0 ||
        playbookGenerationStore.trackStepsFinal[0].isCurrentStep
    ) {
        return null
    }

    switch (playbookGenerationStore.playbookTypeSelected) {
        case 'matchUp':
            return MatchUpPlaybook
        case 'counterAssertion':
            return CounterAssertionPlaybook
        case 'licensingOpportunities':
            return LicensingOpportunitiesPlaybook
        case 'prospectivePatentAnalysis':
            return ProspectivePatentAnalysisPlaybook
        default:
            return null
    }
})

const stepToHideSideNav = computed(() => {
    switch (playbookGenerationStore.playbookTypeSelected) {
        case 'matchUp':
        case 'counterAssertion':
            return 4
        case 'licensingOpportunities':
            return 3
        case 'prospectivePatentAnalysis':
            return 2
        default:
            return null
    }
})

const currentPlaybookStep = computed(() => {
    return playbookGenerationStore.getCurrentStep()
        ? playbookGenerationStore.getCurrentStep().stepNum
        : null
})

const checkIfFitToViewport = computed(() => {
    if (
        playbookGenerationStore.trackStepsFinal.length &&
        playbookGenerationStore.playbookTypeSelected &&
        playbookGenerationStore.playbookTypeSelected === 'licensingOpportunities'
    ) {
        return false
    }

    if (
        !playbookGenerationStore.trackStepsFinal.length ||
        playbookGenerationStore.playbookTypeSelected
    ) {
        return true
    }
})

const resetTargetCompanyStep = () => {
    playbookGenerationStore.error = null
}
</script>

<style lang="scss" scoped>
.playbook-generation-container {
    height: 100%;
    width: 100%;

    .parent-holder {
        &.fit-to-viewport {
            height: calc(100vh - 142px); // height of header + height of playbook nav
        }
    }

    .content-holder {
        height: 100%;
    }

    .ignore-max-width {
        max-width: none !important;
    }

    .prevent-content-overflow {
        max-height: 100%;
        overflow-y: auto;
        overflow-x: hidden;
    }
}
</style>
